import React, { useEffect, useState, useCallback } from 'react';
import Header from './components/Header';
import HeadlineFigures from './components/HeadlineFigures';
import LiabilitiesTable from './components/LiabilitiesTable';
import PaymentsTable from './components/PaymentsTable';
import PassphrasePrompt from './components/PassphrasePrompt';
import { TaxLiability, Payment, TenantConfig, DEFAULT_BRANDING } from './types';
import {
  getPayeLiabilities, getVatLiabilities, getPayments,
  getTenantConfig, verifyPassphrase,
  getStoredToken, storeToken, clearToken,
} from './services/api';
import './App.css';

const App: React.FC = () => {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [needsPassphrase, setNeedsPassphrase] = useState(false);
  const [payeLiabilities, setPayeLiabilities] = useState<TaxLiability[]>([]);
  const [vatLiabilities, setVatLiabilities] = useState<TaxLiability[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (token: string | null) => {
    try {
      setLoading(true);
      setError(null);
      const [paye, vat, pay] = await Promise.all([
        getPayeLiabilities(token),
        getVatLiabilities(token),
        getPayments(token),
      ]);
      setPayeLiabilities(paye.data);
      setVatLiabilities(vat.data);
      setPayments(pay.data);

      const timestamps = [paye.lastUpdated, vat.lastUpdated, pay.lastUpdated].filter(Boolean);
      if (timestamps.length > 0) {
        const latest = timestamps.sort().pop()!;
        setLastUpdated(new Date(latest).toLocaleDateString('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric',
        }) + ', ' + new Date(latest).toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        }));
      } else {
        setLastUpdated(new Date().toLocaleDateString('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric',
        }) + ', ' + new Date().toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        }));
      }
      setNeedsPassphrase(false);
    } catch (err) {
      // If 401, the stored token is invalid/expired — need to re-authenticate
      if (String(err).includes('401')) {
        clearToken();
        setNeedsPassphrase(true);
      } else {
        setNeedsPassphrase(false);
        setError('Failed to load tax data. Please try again later.');
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    async function init() {
      const tenantConfig = await getTenantConfig();
      if (!tenantConfig) {
        setError('This domain is not configured for any tenant. Please check the URL.');
        setLoading(false);
        return;
      }
      setTenant(tenantConfig);

      if (tenantConfig.passwordProtected) {
        const existingToken = getStoredToken();
        if (existingToken) {
          await loadData(existingToken);
        } else {
          setNeedsPassphrase(true);
          setLoading(false);
        }
      } else {
        await loadData(null);
      }
    }
    init();
  }, [loadData]);

  useEffect(() => {
    if (tenant) {
      document.title = `${tenant.displayName} — Tax Liabilities`;
      if (tenant.faviconUrl) {
        const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement
          || document.createElement('link');
        link.rel = 'icon';
        link.href = tenant.faviconUrl;
        document.head.appendChild(link);
      }
      if (tenant.appleTouchIconUrl) {
        const touchIcon = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement
          || document.createElement('link');
        touchIcon.rel = 'apple-touch-icon';
        touchIcon.href = tenant.appleTouchIconUrl;
        document.head.appendChild(touchIcon);
      }
    }
  }, [tenant]);

  const handlePassphraseVerified = async (token: string) => {
    storeToken(token);
    await loadData(token);
  };

  const branding = tenant?.branding || DEFAULT_BRANDING;

  // Show blank loading screen until tenant config is loaded
  if (!tenant && !error) {
    return (
      <div className="app">
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (needsPassphrase && tenant) {
    return (
      <PassphrasePrompt
        displayName={tenant.displayName}
        branding={branding}
        onVerified={handlePassphraseVerified}
        verifyPassphrase={verifyPassphrase}
      />
    );
  }

  return (
    <div className="app">
      <Header branding={branding} displayName={tenant?.displayName} />
      <main className="main-content">
        <h1 className="govuk-heading-xl">Our tax liabilities</h1>
        {lastUpdated && (
          <p className="last-updated">Last updated: {lastUpdated}</p>
        )}
        {tenant?.compliancePackUrl && (
          <p className="compliance-link">
            <a href={tenant.compliancePackUrl} target="_blank" rel="noopener noreferrer">
              Download our compliance pack
            </a>
          </p>
        )}

        {error && (
          <div className="error-banner">
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p className="loading-text">Loading tax data...</p>
          </div>
        ) : !error && (
          <>
            <HeadlineFigures
              payeLiabilities={payeLiabilities}
              vatLiabilities={vatLiabilities}
            />

            <LiabilitiesTable title="PAYE liabilities" liabilities={payeLiabilities} />
            <LiabilitiesTable title="VAT liabilities" liabilities={vatLiabilities} />
            <PaymentsTable payments={payments} />
          </>
        )}

        <div className="info-callout">
          <p>This data is provided by integration with our accounting system and HMRC as well as manual upload by our accounts team. Please <a href={branding.contactUrl || '#'} target="_blank" rel="noopener noreferrer">{branding.contactText}</a> for any queries.</p>
        </div>
      </main>
      <footer
        className="app-footer"
        style={{
          backgroundColor: branding.footerBackgroundColour,
          color: branding.footerTextColour,
        }}
      >
        <div className="footer-content">
          <img src={branding.footerLogoUrl || ''} alt={tenant?.displayName || 'Logo'} className="footer-logo" />
          <p className="footer-copyright">{branding.copyrightText || ''}</p>
        </div>
      </footer>
    </div>
  );
};

export default App;
